import cn from 'classnames';
import type { RefObject } from 'react';
import type { CalendarMode, Timezones } from '../types';
import { useCalculateNowPosition, useGetNowLineStyle, useScrollToNowLine, useShouldShowNowLine } from './hooks';
import styles from './now-line.module.css';

type Properties = {
  bodyReference: RefObject<HTMLDivElement | null>;
  days: Date[];
  mode: CalendarMode;
  primaryTimezone: Timezones[number];
  timezoneCount: number;
};

const NowLine = ({ bodyReference, days, mode, primaryTimezone, timezoneCount }: Properties) => {
  const shouldShowNowLine = useShouldShowNowLine({ days, primaryTimezone });
  const { top } = useCalculateNowPosition({ timezone: primaryTimezone });
  useScrollToNowLine({ bodyReference, enabled: shouldShowNowLine, top });
  const isWeekMode = mode === 'week';
  const { isCurrentDay, nowDotStyle, nowLineStyle } = useGetNowLineStyle({
    days,
    isWeekMode,
    primaryTimezone,
    timezoneCount,
    top,
  });

  if (!shouldShowNowLine) {
    return null;
  }

  return (
    <div
      className={cn(styles['now-line'], {
        [styles['week-mode'] ?? '']: isWeekMode,
      })}
      style={nowLineStyle}
    >
      {isWeekMode ? (
        <div className={styles.segments}>
          {days.map((day) => (
            <div
              className={isCurrentDay(day) ? styles['segment-solid'] : styles['segment-dashed']}
              key={day.toISOString()}
            />
          ))}
        </div>
      ) : null}
      <div className={styles['now-dot']} style={nowDotStyle} />
    </div>
  );
};

export default NowLine;
